/**
 * CASCATA DO CADASTRO DE SUB-OS: OS → PROJETO → UNIDADE/ÁREA.
 *
 * Regras puras da interface, separadas da página para poderem ser exercitadas
 * sem DOM. Elas apenas ESTREITAM o que é oferecido; quem decide se a
 * combinação é aceitável continua sendo o serviço — a tela filtrada não
 * substitui a validação.
 */

import type { ProjectLike, UnitAreaLike, WorkOrderLike, WorkOrderProjectLinkLike } from "./sub-os-context.js";
import { projectsForWorkOrder } from "./sub-os-context.js";

export const NO_PROJECTS_FOR_WORK_ORDER_MESSAGE =
  "Nenhum Projeto vinculado a esta Ordem de Serviço.";
export const NO_UNIT_AREAS_FOR_PROJECT_MESSAGE =
  "Este Projeto não possui Unidades/Áreas cadastradas.";

/** Projetos oferecidos depois de escolher a OS — só os vinculados a ela. */
export function projectOptionsForWorkOrder(
  workOrderId: string,
  workOrders: readonly WorkOrderLike[],
  projects: readonly ProjectLike[],
  links: readonly WorkOrderProjectLinkLike[]
): ProjectLike[] {
  if (!workOrderId) return [];
  const workOrder = workOrders.find(wo => wo.id === workOrderId) ?? null;
  return projectsForWorkOrder(workOrder, projects, links);
}

/** Unidades/Áreas oferecidas depois de escolher o Projeto — só as dele. */
export function unitAreaOptionsForProject(
  projectId: string,
  unitAreas: readonly UnitAreaLike[]
): UnitAreaLike[] {
  if (!projectId) return [];
  return unitAreas
    .filter(area => area.projectId === projectId)
    .sort((a, b) => a.code.localeCompare(b.code, "pt-BR", { numeric: true }));
}

/**
 * Unidade/Área pré-selecionada: com uma única opção não há o que escolher.
 * Com várias, a escolha é do usuário — nada é preenchido por conta própria.
 */
export function autoSelectUnitArea(options: readonly UnitAreaLike[]): string {
  return options.length === 1 ? options[0]!.id : "";
}

/**
 * Trocar a OS invalida o Projeto que não pertença à nova OS.
 * Devolve o Projeto que pode permanecer, ou vazio.
 */
export function keepProjectOnWorkOrderChange(
  currentProjectId: string,
  availableProjects: readonly ProjectLike[]
): string {
  if (!currentProjectId) return "";
  return availableProjects.some(p => p.id === currentProjectId) ? currentProjectId : "";
}

/** Trocar o Projeto invalida a Unidade/Área que não pertença ao novo Projeto. */
export function keepUnitAreaOnProjectChange(
  currentUnitAreaId: string,
  availableUnitAreas: readonly UnitAreaLike[]
): string {
  if (!currentUnitAreaId) return "";
  return availableUnitAreas.some(a => a.id === currentUnitAreaId) ? currentUnitAreaId : "";
}

/** Projeto na lista do modal de vínculo, com o estado já resolvido. */
export interface LinkableProject {
  id: string;
  code: string;
  name: string;
  label: string;
  alreadyLinked: boolean;
}

/**
 * Projetos que o modal "Vincular Projeto" oferece.
 *
 * Os já vinculados continuam visíveis, marcados como tal — some-los
 * esconderia do usuário o que a OS já tem. Eles não podem ser vinculados de
 * novo.
 */
export function linkableProjectsForWorkOrder(input: {
  workOrder: WorkOrderLike | null;
  projects: readonly ProjectLike[];
  links: readonly WorkOrderProjectLinkLike[];
  search?: string;
}): LinkableProject[] {
  const { workOrder, projects, links, search } = input;
  if (!workOrder) return [];

  const linkedIds = new Set(links.filter(l => l.workOrderId === workOrder.id).map(l => l.projectId));
  if (workOrder.projectId) linkedIds.add(workOrder.projectId);

  const term = String(search ?? "").trim().toLowerCase();

  return projects
    // Projeto de outro Cliente não é oferecido para esta OS.
    .filter(project => !workOrder.clientId || project.clientId === workOrder.clientId)
    .map(project => ({
      id: project.id,
      code: project.code,
      name: project.name,
      label: project.code ? `${project.code} — ${project.name}` : project.name,
      alreadyLinked: linkedIds.has(project.id)
    }))
    .filter(option => !term || option.label.toLowerCase().includes(term))
    .sort((a, b) => a.label.localeCompare(b.label, "pt-BR", { numeric: true }));
}

/** Seleção válida do modal: ignora o que já está vinculado. */
export function selectableProjectIds(
  options: readonly LinkableProject[],
  selected: ReadonlySet<string>
): string[] {
  return options
    .filter(option => !option.alreadyLinked && selected.has(option.id))
    .map(option => option.id);
}
